import ArtTypes from '~/constants/types/ArtTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import CatalogTypes from '~/constants/types/CatalogTypes';
import IconTypes from '~/constants/types/IconTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import TextTypes from '~/constants/types/TextTypes';
import UploadTypes from '~/constants/types/UploadTypes';
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import {INK_COLORS} from '~/constants/InkConstants';
import {
  INCOMPATIBLE_NAMES_AND_NUMBERS,
  NAMES_NUMBERS_MULTI_COLOR,
  TOO_MANY_COLORS,
  TOO_MANY_SLEEVE_COLORS
} from '~/constants/NotificationConstants';
import {SLEEVE_COLOR_LIMIT} from '~/constants/ProductConstants';
import {DEFAULT_CONTRAST} from '~/constants/UploadConstants';
import {getIcons} from '~/selectors/IconSelectors';
import {
  getIcons as getCanvasIcons,
  hasNamesAndNumbers,
  selectedViewId,
  getCanvas
} from '~/selectors/CanvasSelectors';
import {getStyles} from '~/selectors/CatalogSelectors';
import {
  allowsNamesAndNumbers,
  getAnchorStyle,
  getDecorationOverrideColor,
  getDecorationOverrideColorForStyle,
  getDecorationOverride,
  getDefaultIconColor,
  getLaserEngravedDesignColor,
  getSelectedProduct,
  getView,
  isAnchorLaserEngraved,
  isAnchorOverColorLimit,
  isAnchorOverSleeveColorLimit
} from '~/selectors/ProductSelectors';
import {
  fitWithinConstraint,
  updateIcon,
  changeSpotColor
} from '~/data/mutators/icon';
import {
  getBackgroundSpotColor,
  isBackgroundVisible,
  isClipartMultiColor,
  isWithinConstraint
} from '~/data/accessors/icon';
// metrics
import {metrics} from '~/vendor/metrics';
import {signaler} from '~/adapters/signaler/signalerFeatureFlags';
import {UPLOAD_ONE_COLOR_FLAG} from '~/adapters/signaler/signalerFeatureFlags';
import {decorationOverridesEnabled} from '~/selectors/ConfigSelectors';
import {matchesDecorationOverride} from '~/utils/decorationOverrides';

function isColorCountAlertRelevant(state) {
  const isDecorationOverrideEnabled = decorationOverridesEnabled(state);
  const decorationOverride = getDecorationOverride(state);

  if (isDecorationOverrideEnabled) {
    return !decorationOverride;
  }

  return !isAnchorLaserEngraved(state);
}

function validateDesignChange(state) {
  const notification =
    validateMultiColorAndNamesNumbers(state) ||
    validateNamesAndNumbers(state) ||
    validateColorCount(state);

  if (notification) {
    return {...state, notification};
  }

  return state;
}

function validateProductSwitch(state, action) {
  const isDecorationOverrideEnabled = decorationOverridesEnabled(state);
  const notification =
    validateMultiColorAndNamesNumbers(state) ||
    validateNamesAndNumbers(state) ||
    (!action.payload.preventHighInkColorAlert && validateColorCount(state));

  const updatedState = isDecorationOverrideEnabled
    ? validateProductWithDecoOverrideSwap(state, action)
    : validateLaserEngravedProductSwap(state, action);

  // Don't fire another notification if laser engraved since it'd overwrite the laser engraved modal
  if (!isAnchorLaserEngraved(state) && notification) {
    return {...updatedState, notification};
  }

  return updatedState;
}

function validateColorCount(state) {
  const style = getAnchorStyle(state);
  if (isAnchorOverColorLimit(state) && isColorCountAlertRelevant(state)) {
    metrics.pageViewTag('ndx/quote/error/color_overload');
    return state.notification.set('notification', {
      key: TOO_MANY_COLORS,
      categoryId: style.primary_category_id,
      colorLimit: style.color_limit
    });
  } else if (isAnchorOverSleeveColorLimit(state)) {
    metrics.pageViewTag('/ndx/sleeve/error/inkcolormax');
    return state.notification.set('notification', {
      key: TOO_MANY_SLEEVE_COLORS,
      colorLimit: SLEEVE_COLOR_LIMIT
    });
  }
}

function validateNamesAndNumbers(state) {
  if (hasNamesAndNumbers(state) && !allowsNamesAndNumbers(state)) {
    return state.notification.set('notification', {
      key: INCOMPATIBLE_NAMES_AND_NUMBERS
    });
  }
}

function validateMultiColorAndNamesNumbers(state) {
  const hasBothErrors =
    Boolean(validateNamesAndNumbers(state)) &&
    Boolean(validateColorCount(state));

  if (hasBothErrors) {
    return state.notification.set('notification', {
      key: NAMES_NUMBERS_MULTI_COLOR
    });
  }
}

function convertImmutableIconToLaserEngraved(icon, laserEngravedDesignColor) {
  switch (icon.get('type')) {
    case 'TEXT': {
      const updatedIconFields = {
        color: laserEngravedDesignColor
      };

      if (icon.get('outlineColor') && icon.get('outlineWidth')) {
        updatedIconFields.outlineColor = laserEngravedDesignColor;
        updatedIconFields.color = INK_COLORS.None;
      }

      return icon.merge(updatedIconFields);
    }
    case 'CLIPART': {
      let newIcon = icon.merge({
        color: laserEngravedDesignColor,
        singleColor: true
      });

      if (isClipartMultiColor(newIcon)) {
        // CGIR does not support keep-white for multicolor cliparts
        newIcon = newIcon.set('transparent', true);
      }

      return newIcon;
    }
    case 'UPLOAD': {
      const uploadOneColorTestValue = signaler.featureFlag(
        UPLOAD_ONE_COLOR_FLAG
      );
      let newIcon = icon.merge({
        color: laserEngravedDesignColor,
        singleColor: true,
        contrast:
          uploadOneColorTestValue === 'test'
            ? icon.get('contrast') || DEFAULT_CONTRAST
            : icon.get('contrast')
      });

      const newIconJs = newIcon.toJS();
      const visible = isBackgroundVisible(newIconJs);
      if (visible) {
        const spotColor = getBackgroundSpotColor(newIconJs);
        newIcon = changeSpotColor(
          newIcon,
          spotColor.original,
          INK_COLORS.None,
          false
        );
      }

      return newIcon;
    }
    default:
      return icon;
  }
}

function revertLaserEngravedImmutableIcon(icon) {
  switch (icon.get('type')) {
    case 'CLIPART':
    case 'UPLOAD':
      return icon.set('singleColor', false);
    default:
      return icon;
  }
}

function updateIconsToMatchDecorationMethod(
  state,
  iconUpdaterFunc,
  color = undefined
) {
  let newIconPresent = state.icon.present;
  const icons = getCanvasIcons(state);
  icons.forEach(icon => {
    newIconPresent = newIconPresent.set(
      icon.get('id'),
      iconUpdaterFunc(icon, color)
    );
  });

  return {
    ...state,
    icon: {
      ...state.icon,
      present: newIconPresent
    }
  };
}

function validateLaserEngravedProductSwap(state, action) {
  const {previousStyleId, nextStyleId} = action.payload;
  const styles = getStyles(state);
  const isPrevStyleLaserEngraved =
    styles.get(previousStyleId)?.decoration_method === 'laser engraved';
  const isNewStyleLaserEngraved =
    styles.get(nextStyleId)?.decoration_method === 'laser engraved';
  const laserEngravedDesignColor = getLaserEngravedDesignColor(state);

  if (isPrevStyleLaserEngraved && !isNewStyleLaserEngraved) {
    return updateIconsToMatchDecorationMethod(
      state,
      revertLaserEngravedImmutableIcon
    );
  } else if (!isPrevStyleLaserEngraved && isNewStyleLaserEngraved) {
    return updateIconsToMatchDecorationMethod(
      state,
      convertImmutableIconToLaserEngraved,
      laserEngravedDesignColor
    );
  }

  return state;
}

function validateSelectedViewId(state) {
  const viewId = selectedViewId(state);
  const style = getSelectedProduct(state);

  if (!style) {
    return state;
  }
  const views = style.views;
  const validatedViewId = views.includes(viewId) ? viewId : views[0].id;

  return {
    ...state,
    canvas: {
      ...state.canvas,
      present: getCanvas(state).set('selectedViewId', validatedViewId)
    }
  };
}

function validateProductWithDecoOverrideSwap(state, action) {
  const {previousStyleId, previousColorId, nextStyleId, nextColorIds} =
    action.payload;
  const styles = getStyles(state);
  const previousStyle = styles.get(previousStyleId);
  const nextStyle = styles.get(nextStyleId);

  if (!previousStyle || !nextStyle) {
    return state;
  }

  const hasPrevStyleDecoOverride = matchesDecorationOverride(
    previousStyle.id,
    previousStyle.decoration_method,
    previousStyle.material
  );
  const hasNewStyleDecoOverride = matchesDecorationOverride(
    nextStyle.id,
    nextStyle.decoration_method,
    previousStyle.material
  );
  const decorationOverrideColor = getDecorationOverrideColorForStyle(state)(
    nextStyle,
    nextColorIds[previousColorId]
  );

  // when previous style had decoration override and the new one doesn't
  // We want to update icons to put them into original state (remove single color)
  if (hasPrevStyleDecoOverride && !hasNewStyleDecoOverride) {
    return updateIconsToMatchDecorationMethod(
      state,
      revertLaserEngravedImmutableIcon
    );
  }

  // When previous style didn't have decoration override but the new one does
  // we want to update the icons to reflect the decoration override (e.g. set single color)
  if (
    !hasPrevStyleDecoOverride &&
    hasNewStyleDecoOverride &&
    decorationOverrideColor
  ) {
    return updateIconsToMatchDecorationMethod(
      state,
      convertImmutableIconToLaserEngraved,
      decorationOverrideColor
    );
  }

  return state;
}

function validateIcons(state, action) {
  const validatedConstraintsState = validateIconFitsConstraints(state, action);
  return validateLaserEngravedIconColors(validatedConstraintsState, action);
}

function validateIconFitsConstraints(state, action) {
  const {iconId} = action.payload;
  const icon = getIcons(state).get(iconId);
  if (!icon) return state;
  const {constraint} = getView(state, {viewIdentifier: icon.viewId});
  const iconWithinConstraints =
    isWithinConstraint('height', icon, constraint) &&
    isWithinConstraint('width', icon, constraint);
  if (iconWithinConstraints) {
    return state;
  } else {
    // dispatch some Canvas action notifying hash we updated the icon.
    return {
      ...state,
      canvas: {
        ...state.canvas,
        present: getCanvas(state).set(
          'currentCanvas',
          getCanvas(state).currentCanvas + 1
        )
      },
      icon: {
        ...state.icon,
        present: state.icon.present.set(
          iconId,
          fitWithinConstraint(icon, constraint)
        )
      }
    };
  }
}

function validateLaserEngravedIconColors(state, action) {
  const {iconId} = action.payload;
  const icon = getIcons(state).get(iconId);
  const laserEngravedDesignColor = getLaserEngravedDesignColor(state);
  const decorationOverrideColor = getDecorationOverrideColor(state);

  if (decorationOverrideColor || isAnchorLaserEngraved(state)) {
    return {
      ...state,
      icon: {
        ...state.icon,
        present: state.icon.present.set(
          iconId,
          convertImmutableIconToLaserEngraved(
            icon,
            decorationOverrideColor || laserEngravedDesignColor
          )
        )
      }
    };
  }

  return state;
}

function validateNonNullColor(state, action) {
  const {iconId} = action.payload;
  const icon = getIcons(state).get(iconId);
  if (!icon) return state;
  const defaultIconColor = getDefaultIconColor(state);
  const outlineWidth = icon.get('outlineWidth');
  const color = icon.get('color');
  if ((!outlineWidth || outlineWidth === 0) && color === INK_COLORS.None) {
    return {
      ...state,
      icon: {
        ...state.icon,
        present: state.icon.present.set(
          iconId,
          updateIcon(icon, 'color', defaultIconColor)
        )
      }
    };
  } else return state;
}

export default function validators(state, action) {
  switch (action.type) {
    case ArtTypes.SWAP:
    case ArtTypes.ADD_ART:
    case IconTypes.PASTE:
    case TextTypes.ADD_TEXT:
    case AnalyticsTypes.UPLOAD_RECENT:
    case UploadTypes.UPLOAD_COMPLETE:
      return validateDesignChange(state);
    case ProductTypes.SWAP_PRODUCT:
      return validateProductSwitch(state, action);
    case CatalogTypes.STYLE_LOADED:
    case CatalogTypes.STYLES_SUCCESS:
      return validateSelectedViewId(state);
    case CanvasTypes.ICON_RENDERED:
    case TextTypes.VERIFY_SUCCESS:
      return validateIcons(state, action);
    case TextTypes.UPDATE_FONT:
      return validateNonNullColor(state, action);
    default:
      return state;
  }
}
